// src/pages/Privacy.jsx
import React from "react";

export default function Privacy() {
  React.useEffect(() => {
    document.title = "Privacy Policy";
  }, []);

  const [isSmallScreen, setIsSmallScreen] = React.useState(false);
  const [openIndex, setOpenIndex] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const pageStyle = {
    ...styles.page,
    ...(isSmallScreen ? styles.pageMobile : null),
  };

  const titleStyle = {
    ...styles.title,
    ...(isSmallScreen ? styles.titleMobile : null),
  };

  const trustGridStyle = {
    ...styles.trustGrid,
    ...(isSmallScreen ? styles.trustGridMobile : null),
  };

  const layoutStyle = {
    ...styles.layout,
    ...(isSmallScreen ? styles.layoutMobile : null),
  };

  const footerGridStyle = {
    ...styles.footerGrid,
    ...(isSmallScreen ? styles.footerGridMobile : null),
  };

  const indexItems = [
    ["information", "Information We Collect"],
    ["use", "How We Use Information"],
    ["ai", "AI Processing & Service Providers"],
    ["products", "Product-Specific Notes"],
    ["cookies", "Cookies & Tracking"],
    ["sharing", "Sharing of Information"],
    ["security", "Data Security"],
    ["retention", "Data Retention"],
    ["transfers", "International Transfers"],
    ["rights", "Your Rights"],
    ["california", "California Privacy Notice"],
    ["children", "Children"],
    ["contact", "Contact Us"],
  ];

  return (
    <div style={pageStyle}>
      <section style={styles.header}>
        <h1 style={titleStyle}>Privacy Policy</h1>

        <div style={styles.metaRow}>
          <span><strong>Effective Date:</strong> September 29, 2025</span>
          <span style={styles.metaDivider}>|</span>
          <span><strong>Last Updated:</strong> September 29, 2025</span>
        </div>

        <p style={styles.intro}>
          This Privacy Policy explains how <strong>NexGen Automate, Inc.</strong> (“Company,” “we,”
          “us,” or “our”) collects, uses, discloses, and protects information when you use
          AskLawly.com, our mobile apps, and related tools and services (the “Services”).
        </p>
      </section>

      <section style={trustGridStyle} aria-label="Privacy commitments">
        <TrustItem
          icon="▣"
          title="We do not sell"
          text="We do not sell personal information."
        />
        <TrustItem
          icon="◇"
          title="No third-party marketing"
          text="We do not use your data for third-party marketing."
        />
        <TrustItem
          icon="◎"
          title="Purpose-limited use"
          text="We use data to provide, secure, improve, and support our Services."
        />
      </section>

      {isSmallScreen && (
        <section style={styles.mobileIndex}>
          <button
            type="button"
            style={styles.mobileIndexButton}
            onClick={() => setOpenIndex((prev) => !prev)}
            aria-expanded={openIndex}
          >
            <span>In This Policy</span>
            <span>{openIndex ? "−" : "⌄"}</span>
          </button>
          {openIndex && (
            <div style={styles.mobileIndexList}>
              {indexItems.map(([id, label]) => (
                <a key={id} href={`#${id}`} style={styles.mobileIndexLink}>
                  {label}
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      <div style={layoutStyle}>
        {!isSmallScreen && (
          <aside style={styles.sidebar}>
            <div style={styles.sidebarTitle}>In This Policy</div>
            <nav style={styles.navList}>
              {indexItems.map(([id, label], index) => (
                <a
                  key={id}
                  href={`#${id}`}
                  style={{
                    ...styles.navLink,
                    ...(index === 0 ? styles.navLinkActive : null),
                  }}
                >
                  <span style={styles.navIcon} aria-hidden="true">•</span>
                  <span>{label}</span>
                </a>
              ))}
            </nav>

            <div style={styles.sidebarNote}>
              <div style={styles.sidebarNoteIcon}>▣</div>
              <div>
                <strong>Your trust matters.</strong>
                <p style={styles.sidebarNoteText}>
                  We aim to explain our data practices clearly and keep privacy choices accessible.
                </p>
              </div>
            </div>
          </aside>
        )}

        <main style={styles.policyPanel}>
          <PolicySection id="information" number="1" title="Information We Collect">
            <p style={styles.p}>
              We collect information you provide directly, information created through your use of
              the Services, information collected automatically, and limited information from
              service providers or integrations you choose to use.
            </p>
            <ul style={styles.list}>
              <li style={styles.li}><strong>Account & Contact:</strong> name, email, password hash, optional phone number, profile information, account settings, and communications with us.</li>
              <li style={styles.li}><strong>Usage & Content:</strong> chat messages, prompts, DocWriter inputs/outputs, Contract Insight uploads/reports, Case Estimator/Case Value inputs, Smart Alert configurations, Vision Assist activity, and related metadata.</li>
              <li style={styles.li}><strong>FileVault Content:</strong> user-uploaded documents and file names, sizes, types, timestamps, and storage metadata.</li>
              <li style={styles.li}><strong>Device/Technical:</strong> IP address, device/browser type, operating system, language, cookies, diagnostics, crash/error logs, approximate location inferred from IP, and security events.</li>
              <li style={styles.li}><strong>Payments:</strong> limited billing and subscription data from payment processors. We do not store full payment card numbers.</li>
              <li style={styles.li}><strong>Smart Alert Contacts:</strong> names, phone numbers, and emails you choose to notify. You are responsible for having permission to provide those contacts.</li>
              <li style={styles.li}><strong>Information from service providers:</strong> authentication, billing, analytics, security, support, email/SMS delivery, hosting, storage, and connected integrations.</li>
            </ul>
          </PolicySection>

          <Callout
            id="sensitive"
            icon="!"
            title="Sensitive information and documents"
            tone="blue"
          >
            <p style={styles.calloutText}>
              The Services may process legal, financial, tax, immigration, business, health-adjacent,
              identity, or other sensitive information if you choose to submit it. Please avoid
              submitting unnecessary sensitive information. When sensitive information is submitted,
              we use it to provide the Services, secure the platform, comply with law, enforce our
              Terms, and improve reliability consistent with this Policy.
            </p>
          </Callout>

          <PolicySection id="use" number="2" title="How We Use Information">
            <ul style={styles.list}>
              <li style={styles.li}>To operate and deliver core features, including Chat, DocWriter, Contract Insight, Vision Assist, FileVault, Smart Alert, Case Estimator, and Case Value.</li>
              <li style={styles.li}>To generate answers, drafts, contract reports, summaries, estimates, screen guidance, document guidance, alerts, and other outputs requested by users.</li>
              <li style={styles.li}>To authenticate users, secure accounts, detect and prevent abuse, fraud, spam, unauthorized access, and security incidents.</li>
              <li style={styles.li}>To troubleshoot, debug, measure reliability, improve product quality, and perform limited human review where needed for safety, quality, abuse prevention, support, or legal compliance.</li>
              <li style={styles.li}>To communicate with you about service notices, updates, billing, support, security alerts, account status, and policy changes.</li>
              <li style={styles.li}>To comply with law, enforce our Terms, resolve disputes, and protect rights, safety, and property.</li>
            </ul>
          </PolicySection>

          <PolicySection id="ai" number="3" title="AI Processing & Service Providers">
            <p style={styles.p}>
              AskLawly uses AI models and trusted service providers to process prompts, uploaded
              documents, screen-share context, screenshots, generated outputs, and related metadata
              so we can provide the Services.
            </p>
            <ul style={styles.list}>
              <li style={styles.li}>Your inputs and content may be transmitted to AI service providers, cloud providers, storage providers, analytics/security tools, payment processors, and email/SMS providers as needed to deliver the Services.</li>
              <li style={styles.li}>We require service providers to process information for our business purposes and under appropriate confidentiality, security, and data protection obligations.</li>
              <li style={styles.li}>We do not use your content for third-party advertising. We also do not permit service providers to use your content to train third-party models unless we disclose that practice or obtain appropriate permission.</li>
              <li style={styles.li}>AI outputs may be inaccurate or incomplete. You should review outputs carefully and consult qualified professionals when appropriate.</li>
            </ul>
          </PolicySection>

          <PolicySection id="products" number="4" title="Product-Specific Notes">
            <ul style={styles.list}>
              <li style={styles.li}><strong>Chat:</strong> messages and prompts are processed to generate responses and may be logged to improve reliability, safety, and quality. We do not use your content for third-party advertising.</li>
              <li style={styles.li}><strong>Contract Insight:</strong> uploaded contracts and related documents are processed to extract key terms, risks, summaries, and reports. We may store processing metadata, generated reports, and related logs to deliver the feature, improve reliability, and prevent abuse.</li>
              <li style={styles.li}><strong>DocWriter:</strong> your selections, inputs, attachments, and generated drafts are processed to create outputs based on the options you select. Drafts may be saved to your account when you choose to store them.</li>
              <li style={styles.li}><strong>Vision Assist:</strong> if you choose screen sharing, screenshots, screen context, or uploaded documents, that content may be processed in real time or in segments to provide guidance. Do not share passwords, full payment card numbers, or unnecessary sensitive information.</li>
              <li style={styles.li}><strong>FileVault:</strong> files are stored with security controls, including encryption at rest where supported. Availability is not guaranteed; keep your own backups. We may scan or review files for security, abuse, and Terms compliance.</li>
              <li style={styles.li}><strong>Smart Alert:</strong> we process and transmit messages to your designated recipients through email/SMS providers. Delivery is not guaranteed and depends on carriers, networks, third-party providers, and recipient settings. You are responsible for obtaining consent where required.</li>
              <li style={styles.li}><strong>Case Estimator & Case Value:</strong> your inputs are used to generate directional estimates and may be logged to improve accuracy, safety, and user experience.</li>
            </ul>
          </PolicySection>

          <PolicySection id="cookies" number="5" title="Cookies & Tracking">
            <p style={styles.p}>
              We use essential cookies and similar technologies for login, authentication, session
              management, security, fraud prevention, and service operation. We may also use
              analytics cookies or similar tools to understand usage, diagnose errors, and improve
              performance. You can control cookies through your browser, but blocking some cookies
              may limit functionality.
            </p>
            <p style={styles.p}>
              We do not sell personal information or share personal information for cross-context
              behavioral advertising. If we later introduce advertising or additional tracking that
              changes this position, we will update this Policy and provide any legally required
              choices.
            </p>
          </PolicySection>

          <PolicySection id="sharing" number="6" title="Sharing of Information">
            <ul style={styles.list}>
              <li style={styles.li}><strong>Service Providers:</strong> cloud hosting, storage, AI processing, analytics, security, authentication, payments, SMS/email delivery, customer support, and operational tools.</li>
              <li style={styles.li}><strong>Legal & Safety:</strong> when required by law, legal process, regulators, government requests, or to protect users, the public, our Services, or our rights.</li>
              <li style={styles.li}><strong>Business Transfers:</strong> in connection with a merger, acquisition, financing, reorganization, bankruptcy, or asset sale, subject to this Policy or a successor privacy notice.</li>
              <li style={styles.li}><strong>With your direction:</strong> when you ask us to share content, send alerts, connect services, generate share links, or transmit information to a third party.</li>
              <li style={styles.li}><strong>No Sale/Share for Ads:</strong> we do not sell personal information or share it for cross-context behavioral advertising.</li>
            </ul>
          </PolicySection>

          <PolicySection id="security" number="7" title="Data Security">
            <p style={styles.p}>
              We use reasonable technical and organizational safeguards, including access controls,
              encryption where appropriate, logging, monitoring, and security review practices. No
              system can be guaranteed 100% secure. You are responsible for protecting your account
              credentials, using strong passwords, maintaining independent backups of important
              files, and avoiding unnecessary sensitive disclosures.
            </p>
          </PolicySection>

          <PolicySection id="retention" number="8" title="Data Retention">
            <p style={styles.p}>
              We retain information for as long as needed to provide the Services, maintain accounts,
              satisfy legal, tax, accounting, security, fraud-prevention, dispute-resolution, and
              operational needs, and enforce our Terms.
            </p>
            <p style={styles.p}>
              Typical retention may include account and billing records for the life of the account
              plus a limited period; security logs and diagnostics for a limited rolling window;
              Smart Alert logs for audit and abuse prevention; generated reports and drafts while
              associated with your account; and backups for limited durations before deletion or
              overwrite. If you request deletion, some information may remain temporarily in backups
              or be retained where legally or operationally necessary.
            </p>
          </PolicySection>

          <PolicySection id="transfers" number="9" title="International Transfers">
            <p style={styles.p}>
              NexGen Automate, Inc. is formed in Delaware and operates from the United States. Users
              may access the Services from California, other U.S. states, and countries around the
              world. We may process and store information in the United States and other countries
              where we or our service providers operate. Where required, we use appropriate
              safeguards for cross-border transfers.
            </p>
          </PolicySection>

          <PolicySection id="rights" number="10" title="Your Rights">
            <p style={styles.p}>
              Depending on your location, you may have rights to access, correct, delete, port, or
              receive a copy of your data; to object to or restrict certain processing; to withdraw
              consent where processing is based on consent; and to opt out of sale or sharing of
              personal information, if applicable. We do not sell personal information or share it for
              cross-context behavioral advertising.
            </p>
            <p style={styles.p}>
              To exercise privacy rights, contact{" "}
              <a href="mailto:support@asklawly.com" style={styles.link}>support@asklawly.com</a>.
              We may need to verify your identity, account ownership, request details, and
              jurisdiction before completing a request.
            </p>
          </PolicySection>

          <Callout
            id="california"
            icon="✓"
            title="California, U.S. state, and GDPR/EEA rights"
            tone="green"
          >
            <p style={styles.calloutText}>
              If you are a California resident, located in another U.S. state with privacy rights,
              or located in the EEA, UK, or Switzerland, you may have additional rights. These may
              include rights to know/access, correct, delete, portability, restriction, objection,
              consent withdrawal, and non-discrimination for exercising privacy rights.
            </p>
            <p style={styles.calloutText}>
              For California residents, our “no sale/share for ads” position means we do not sell
              personal information or share it for cross-context behavioral advertising. If we
              process sensitive personal information, we do so to provide, secure, and improve the
              Services, comply with law, and prevent abuse, unless otherwise disclosed.
            </p>
          </Callout>

          <PolicySection id="legal-bases" number="11" title="Legal Bases for Processing">
            <p style={styles.p}>
              Where laws such as the GDPR require a legal basis, we process personal information
              based on one or more of the following: performance of a contract with you; our
              legitimate interests in providing, securing, improving, and supporting the Services;
              consent where required; and compliance with legal obligations.
            </p>
          </PolicySection>

          <PolicySection id="dnt" number="12" title="Do Not Track & Global Privacy Controls">
            <p style={styles.p}>
              Some browsers offer “Do Not Track” or similar signals. Because there is no uniform
              industry standard for these signals, we may not respond to all such signals. Where
              legally required and technically feasible, we will honor recognized opt-out preference
              signals such as Global Privacy Control for applicable choices.
            </p>
          </PolicySection>

          <PolicySection id="children" number="13" title="Children">
            <p style={styles.p}>
              AskLawly is not directed to children under 13 or the age required by local law. Do not
              register or submit data if you are under that age. If we learn that we have collected
              personal information from a child without required consent, we will take appropriate
              steps to delete it.
            </p>
          </PolicySection>

          <PolicySection id="links" number="14" title="Links & Third-Party Sites">
            <p style={styles.p}>
              The Services may link to third-party sites or services. Their privacy practices are
              governed by their own policies. We are not responsible for their content, security, or
              privacy practices.
            </p>
          </PolicySection>

          <PolicySection id="changes" number="15" title="Changes to This Policy">
            <p style={styles.p}>
              We may update this Policy from time to time. Material changes will be indicated by
              updating the “Last Updated” date above or providing in-product notice where
              appropriate. Your continued use of the Services after changes means you accept the
              updated Policy.
            </p>
          </PolicySection>

        </main>
      </div>

      <section style={styles.footerPanel}>
        <h2 style={styles.footerTitle}>Ownership & Contact</h2>
        <div style={footerGridStyle}>
          <FooterItem icon="▥" text={<><strong>NexGen Automate, Inc.</strong></>} />
          <FooterItem icon="◎" text={<><strong>AskLawly.com</strong></>} />
          <FooterItem icon="⌖" text="Wilmington, New Castle County, Delaware, USA" />
          <FooterItem icon="◌" text={<><strong>Founder:</strong> Imran Ahmed, CPA</>} />
          <FooterItem
            icon="✉"
            text={
              <a style={styles.link} href="mailto:support@asklawly.com">
                support@asklawly.com
              </a>
            }
          />
        </div>
        <div style={styles.footerLinks}>
          <a style={styles.link} href="/terms">Terms of Use</a>
          <span style={styles.footerDivider}>|</span>
          <a style={styles.link} href="/privacy">Privacy Policy</a>
        </div>
      </section>
    </div>
  );
}

const TrustItem = ({ icon, title, text }) => (
  <article style={styles.trustItem}>
    <div style={styles.trustIcon}>{icon}</div>
    <div>
      <h2 style={styles.trustTitle}>{title}</h2>
      <p style={styles.trustText}>{text}</p>
    </div>
  </article>
);

const PolicySection = ({ id, number, title, children }) => (
  <section id={id} style={styles.policySection}>
    <h2 style={styles.sectionTitle}>{number}) {title}</h2>
    {children}
  </section>
);

const Callout = ({ id, icon, title, tone, children }) => {
  const isGreen = tone === "green";
  return (
    <section
      id={id}
      style={{
        ...styles.callout,
        ...(isGreen ? styles.calloutGreen : null),
      }}
    >
      <div
        style={{
          ...styles.calloutIcon,
          ...(isGreen ? styles.calloutIconGreen : null),
        }}
      >
        {icon}
      </div>
      <div>
        <h2
          style={{
            ...styles.calloutTitle,
            ...(isGreen ? styles.calloutTitleGreen : null),
          }}
        >
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
};

const FooterItem = ({ icon, text }) => (
  <div style={styles.footerItem}>
    <span style={styles.footerIcon}>{icon}</span>
    <span>{text}</span>
  </div>
);

const styles = {
  page: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "18px 16px 44px",
    boxSizing: "border-box",
    background: "#F5F7FA",
    fontFamily: "sans-serif",
  },
  pageMobile: {
    padding: "14px 12px 34px",
  },

  header: {
    padding: "12px 0 14px",
  },
  title: {
    margin: 0,
    color: "#0F172A",
    fontSize: 34,
    lineHeight: 1.08,
    fontWeight: 950,
    letterSpacing: "-0.9px",
  },
  titleMobile: {
    fontSize: 30,
    textAlign: "center",
    letterSpacing: "-0.65px",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 10,
    color: "#475569",
    fontSize: 13.5,
    lineHeight: 1.4,
    fontWeight: 750,
  },
  metaDivider: {
    color: "#CBD5E1",
  },
  intro: {
    margin: "12px 0 0",
    maxWidth: 880,
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.6,
    fontWeight: 650,
  },

  trustGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 0,
    marginTop: 10,
    borderRadius: 18,
    border: "1px solid rgba(37,99,235,0.16)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    overflow: "hidden",
  },
  trustGridMobile: {
    gridTemplateColumns: "1fr",
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    minHeight: 88,
    padding: 18,
    borderRight: "1px solid rgba(2,6,23,0.08)",
    boxSizing: "border-box",
  },
  trustIcon: {
    width: 48,
    height: 48,
    minWidth: 48,
    borderRadius: 16,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    fontWeight: 950,
  },
  trustTitle: {
    margin: "0 0 4px",
    color: "#0F172A",
    fontSize: 14.5,
    lineHeight: 1.25,
    fontWeight: 950,
  },
  trustText: {
    margin: 0,
    color: "#475569",
    fontSize: 13.25,
    lineHeight: 1.45,
    fontWeight: 650,
  },

  mobileIndex: {
    marginTop: 14,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 10px 26px rgba(2,6,23,0.05)",
    overflow: "hidden",
  },
  mobileIndexButton: {
    appearance: "none",
    width: "100%",
    border: "none",
    background: "#FFFFFF",
    color: "#0F172A",
    padding: "13px 14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    fontSize: 14.5,
    fontWeight: 950,
  },
  mobileIndexList: {
    borderTop: "1px solid rgba(2,6,23,0.08)",
    display: "grid",
    gap: 0,
  },
  mobileIndexLink: {
    padding: "10px 14px",
    color: "#2563EB",
    fontSize: 13.5,
    fontWeight: 800,
    textDecoration: "none",
    borderBottom: "1px solid rgba(2,6,23,0.06)",
  },

  layout: {
    display: "grid",
    gridTemplateColumns: "270px minmax(0, 1fr)",
    gap: 14,
    alignItems: "start",
    marginTop: 14,
  },
  layoutMobile: {
    display: "block",
  },

  sidebar: {
    position: "sticky",
    top: 16,
    alignSelf: "start",
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    padding: 16,
    boxSizing: "border-box",
  },
  sidebarTitle: {
    color: "#0F172A",
    fontSize: 16,
    fontWeight: 950,
    marginBottom: 12,
  },
  navList: {
    display: "grid",
    gap: 5,
  },
  navLink: {
    display: "grid",
    gridTemplateColumns: "26px minmax(0, 1fr)",
    alignItems: "center",
    gap: 9,
    padding: "9px 8px",
    borderRadius: 12,
    color: "#334155",
    fontSize: 13.25,
    lineHeight: 1.3,
    fontWeight: 800,
    textDecoration: "none",
  },
  navLinkActive: {
    background: "rgba(37,99,235,0.08)",
    color: "#2563EB",
  },
  navIcon: {
    width: 22,
    height: 22,
    borderRadius: 8,
    background: "rgba(30,42,120,0.07)",
    color: "#1E2A78",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
  },
  sidebarNote: {
    display: "grid",
    gridTemplateColumns: "32px minmax(0, 1fr)",
    gap: 10,
    marginTop: 16,
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(37,99,235,0.14)",
    background: "rgba(37,99,235,0.045)",
    color: "#334155",
    fontSize: 12.5,
    lineHeight: 1.45,
    fontWeight: 650,
  },
  sidebarNoteIcon: {
    width: 28,
    height: 28,
    borderRadius: 10,
    color: "#2563EB",
    background: "rgba(37,99,235,0.10)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 950,
  },
  sidebarNoteText: {
    margin: "4px 0 0",
  },

  policyPanel: {
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 14px 34px rgba(2,6,23,0.06)",
    padding: 20,
    boxSizing: "border-box",
  },
  policySection: {
    paddingTop: 6,
    paddingBottom: 10,
    borderBottom: "1px solid rgba(2,6,23,0.08)",
    scrollMarginTop: 16,
  },
  sectionTitle: {
    margin: "0 0 10px",
    color: "#0F172A",
    fontSize: 20,
    lineHeight: 1.25,
    fontWeight: 950,
    letterSpacing: "-0.3px",
  },
  p: {
    margin: "8px 0",
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.65,
    fontWeight: 650,
  },
  list: {
    margin: "9px 0",
    paddingLeft: 20,
    color: "#334155",
    fontSize: 14.5,
    lineHeight: 1.62,
    fontWeight: 650,
  },
  li: {
    margin: "7px 0",
    paddingLeft: 2,
  },

  callout: {
    display: "grid",
    gridTemplateColumns: "42px minmax(0, 1fr)",
    gap: 12,
    margin: "14px 0",
    padding: 15,
    borderRadius: 16,
    border: "1px solid rgba(37,99,235,0.18)",
    background: "rgba(37,99,235,0.045)",
    scrollMarginTop: 16,
  },
  calloutGreen: {
    border: "1px solid rgba(16,185,129,0.22)",
    background: "rgba(16,185,129,0.065)",
  },
  calloutIcon: {
    width: 36,
    height: 36,
    borderRadius: 13,
    background: "rgba(37,99,235,0.10)",
    color: "#2563EB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    fontWeight: 950,
  },
  calloutIconGreen: {
    background: "rgba(16,185,129,0.12)",
    color: "#059669",
  },
  calloutTitle: {
    margin: "0 0 6px",
    color: "#2563EB",
    fontSize: 15,
    lineHeight: 1.25,
    fontWeight: 950,
  },
  calloutTitleGreen: {
    color: "#047857",
  },
  calloutText: {
    margin: "7px 0 0",
    color: "#334155",
    fontSize: 13.75,
    lineHeight: 1.58,
    fontWeight: 650,
  },

  contactBox: {
    marginTop: 14,
    padding: 16,
    borderRadius: 16,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#F8FAFC",
    scrollMarginTop: 16,
  },
  contactTitle: {
    margin: "0 0 8px",
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
  },

  footerPanel: {
    marginTop: 14,
    padding: 18,
    borderRadius: 18,
    border: "1px solid rgba(2,6,23,0.10)",
    background: "#FFFFFF",
    boxShadow: "0 12px 30px rgba(2,6,23,0.055)",
    textAlign: "center",
  },
  footerTitle: {
    margin: "0 0 13px",
    color: "#0F172A",
    fontSize: 18,
    fontWeight: 950,
    letterSpacing: "-0.25px",
  },
  footerGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
    alignItems: "center",
  },
  footerGridMobile: {
    gridTemplateColumns: "1fr",
  },
  footerItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 7,
    minHeight: 34,
    color: "#334155",
    fontSize: 12.8,
    lineHeight: 1.35,
    fontWeight: 700,
  },
  footerIcon: {
    color: "#2563EB",
    fontSize: 15,
    fontWeight: 950,
  },
  footerLinks: {
    marginTop: 13,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    fontSize: 13.5,
    fontWeight: 850,
  },
  footerDivider: {
    color: "#CBD5E1",
  },
  link: {
    color: "#2563EB",
    fontWeight: 850,
    textDecoration: "none",
  },
};
